// View-state math for the wizard's interactive geo pickers (states + DMAs),
// extracted so the fit logic is unit-testable without a WebGL canvas
// (WebMercatorViewport is pure math).
import { WebMercatorViewport } from '@deck.gl/core';
import { NAME_TO_ABBR } from './usMapProjection';

export interface MapViewState {
  longitude: number;
  latitude: number;
  zoom: number;
}

// The tuned national framing used when a station has no declared coverage.
export const NATIONAL_VIEW: MapViewState = { longitude: -97, latitude: 37, zoom: 2.8 };

// Cap zoom-in so a station covering one small region (RI, a single DMA)
// still shows surrounding context instead of filling the frame with a
// single polygon. DMAs are smaller units than states, so their picker
// passes a higher cap.
const DEFAULT_MAX_ZOOM = 6;

// Feature-key extractors for the two geo pickers. The states GeoJSON keys
// features by full name; DMA boundaries carry the 3-digit Nielsen code.
export const stateKeyOf = (f: GeoJSON.Feature): string | null =>
  NAME_TO_ABBR[(f.properties as Record<string, string> | null)?.name ?? ''] ?? null;
export const dmaKeyOf = (f: GeoJSON.Feature): string | null => {
  const code = (f.properties as Record<string, unknown> | null)?.dma_code;
  return code ? String(code) : null;
};

// Walk a feature's coordinate tree and accumulate min/max lng+lat. Handles
// Polygon + MultiPolygon (everything we'd see in the US GeoJSON sources).
// Positive longitudes are skipped: after the AK/HI repositioning every US
// coordinate is negative except the far-Aleutian islands past the dateline,
// which would otherwise blow the bbox out to span the whole globe.
export function expandBbox(
  geom: GeoJSON.Geometry,
  bbox: [number, number, number, number],
): void {
  const visit = (c: unknown): void => {
    if (typeof (c as number[])[0] === 'number') {
      const [lng, lat] = c as [number, number];
      if (lng > 0) return;
      if (lng < bbox[0]) bbox[0] = lng;
      if (lat < bbox[1]) bbox[1] = lat;
      if (lng > bbox[2]) bbox[2] = lng;
      if (lat > bbox[3]) bbox[3] = lat;
      return;
    }
    for (const child of c as unknown[]) visit(child);
  };
  if (geom.type === 'Polygon' || geom.type === 'MultiPolygon') {
    visit(geom.coordinates);
  }
}

// Compute the (static) view for a geo picker at the given container size.
// `fitKeys` is the set of features the view should frame, matched via
// `keyOf`; when null/empty the map shows the national framing. Padding
// scales down with the container so fitBounds never throws on small
// screens (2 * padding must stay well under the smaller dimension).
// `bboxMarginPct` widens the framed bbox by that fraction of its own span on
// every side before fitting, so a ring of non-fit context stays visible; the
// pixel padding alone shrinks relative to the content at larger container
// sizes, which framed the fit set edge to edge (CTV-705).
export function computeFitViewState(
  geojson: GeoJSON.FeatureCollection | null,
  fitKeys: string[] | null | undefined,
  keyOf: (f: GeoJSON.Feature) => string | null,
  width: number,
  height: number,
  opts?: { maxZoom?: number; bboxMarginPct?: number },
): MapViewState {
  if (!geojson || !fitKeys || fitKeys.length === 0 || width <= 0 || height <= 0) {
    return NATIONAL_VIEW;
  }
  const fit = new Set(fitKeys);
  const bbox: [number, number, number, number] = [180, 90, -180, -90];
  for (const feat of geojson.features) {
    const key = keyOf(feat);
    if (!key || !fit.has(key)) continue;
    if (feat.geometry) expandBbox(feat.geometry, bbox);
  }
  // No matched features: fall back to national.
  if (bbox[0] > bbox[2] || bbox[1] > bbox[3]) return NATIONAL_VIEW;
  const margin = opts?.bboxMarginPct ?? 0;
  if (margin > 0) {
    const lonPad = (bbox[2] - bbox[0]) * margin;
    const latPad = (bbox[3] - bbox[1]) * margin;
    bbox[0] = Math.max(-180, bbox[0] - lonPad);
    bbox[1] = Math.max(-85, bbox[1] - latPad);
    bbox[2] = Math.min(180, bbox[2] + lonPad);
    bbox[3] = Math.min(85, bbox[3] + latPad);
  }
  const padding = Math.min(40, Math.floor(Math.min(width, height) / 8));
  try {
    const fitted = new WebMercatorViewport({ width, height }).fitBounds(
      [
        [bbox[0], bbox[1]],
        [bbox[2], bbox[3]],
      ],
      { padding },
    );
    return {
      longitude: fitted.longitude,
      latitude: fitted.latitude,
      zoom: Math.min(fitted.zoom, opts?.maxZoom ?? DEFAULT_MAX_ZOOM),
    };
  } catch {
    return NATIONAL_VIEW;
  }
}

// States wrapper, fits the selectable states (coverage + neighbors).
export function computeStatesMapViewState(
  geojson: GeoJSON.FeatureCollection | null,
  fitAbbrs: string[] | null | undefined,
  width: number,
  height: number,
): MapViewState {
  return computeFitViewState(geojson, fitAbbrs, stateKeyOf, width, height);
}

// The lng/lat bounds actually visible at a view state + container size.
// Used to decide which labels are in-frame and where to relocate labels of
// partially-visible regions.
export function visibleBounds(
  view: MapViewState,
  width: number,
  height: number,
): [number, number, number, number] | null {
  if (width <= 0 || height <= 0) return null;
  try {
    return new WebMercatorViewport({
      width,
      height,
      longitude: view.longitude,
      latitude: view.latitude,
      zoom: view.zoom,
    }).getBounds();
  } catch {
    return null;
  }
}
